import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { SupportService } from '../../../services/support/support.service';
import { PageTitleService, ToastService } from '../../../shared/services';
import { SupportTicket, SupportTicketMessage, SupportTicketStatus } from '../../../shared/interfaces';
import { Modal } from '../../../shared/components/modal/modal';
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal';
import { SupportTicketForm, SupportTicketFormValue } from './support-ticket-form/support-ticket-form';
import { SupportThread } from './support-thread/support-thread';
import {
  SUPPORT_STEPS,
  categoryLabel,
  impactLabel,
  statusLabel,
  ticketCode,
} from './support-labels';

type SupportFilter = 'active' | 'finished';
type StepState = 'done' | 'current' | 'todo';

const FINISHED: SupportTicketStatus[] = ['resolved', 'closed'];

@Component({
  selector: 'app-support',
  imports: [DatePipe, FormsModule, Modal, ConfirmModal, SupportThread],
  templateUrl: './support.html',
  styleUrl: './support.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Support implements OnInit {
  private readonly supportService = inject(SupportService);
  private readonly titleService = inject(PageTitleService);
  private readonly toastService = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly contactEmail = 'contacto@citalinkpro.com';
  readonly steps = SUPPORT_STEPS;

  readonly tickets = signal<SupportTicket[]>([]);
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly saving = signal(false);
  readonly filter = signal<SupportFilter>('active');
  readonly selectedId = signal<string | null>(null);

  readonly modalOpen = signal(false);
  readonly editingTicket = signal<SupportTicket | null>(null);
  readonly confirmDeleteOpen = signal(false);
  readonly confirmCloseOpen = signal(false);

  // Conversation of the open ticket
  readonly messages = signal<SupportTicketMessage[]>([]);
  readonly messagesLoading = signal(false);
  readonly replyText = signal('');
  readonly sendingReply = signal(false);
  readonly replyMax = 4000;

  readonly TicketForm = SupportTicketForm;
  modalBodyInputs: Record<string, unknown> = {};

  private readonly detailCloseBtn = viewChild<ElementRef<HTMLButtonElement>>('detailCloseBtn');
  private readonly replyBox = viewChild<ElementRef<HTMLTextAreaElement>>('replyBox');
  private returnFocusTo: HTMLElement | null = null;

  readonly selected = computed(
    () => this.tickets().find((t) => t.supportTicketId === this.selectedId()) ?? null
  );

  readonly activeTickets = computed(() => this.tickets().filter((t) => !FINISHED.includes(t.status)));
  readonly finishedTickets = computed(() => this.tickets().filter((t) => FINISHED.includes(t.status)));
  readonly waitingTickets = computed(() => this.tickets().filter((t) => t.status === 'waiting_user'));

  readonly visibleTickets = computed(() =>
    this.filter() === 'active' ? this.activeTickets() : this.finishedTickets()
  );

  readonly emptyState = computed(() => {
    if (this.tickets().length === 0) {
      return {
        title: 'No tienes tickets de soporte',
        text: 'Si algo no funciona como esperas o tienes una duda, cuéntanos. Te respondemos por correo.',
        action: true,
      };
    }
    return this.filter() === 'active'
      ? {
          title: 'No tienes casos abiertos',
          text: 'Todo en orden. Los tickets resueltos o cerrados están en la pestaña Cerrados.',
          action: true,
        }
      : {
          title: 'Aún no se ha cerrado ningún caso',
          text: 'Cuando resolvamos un ticket o lo cierres tú, aparece aquí.',
          action: false,
        };
  });

  readonly modalTitle = computed(() => {
    const ticket = this.editingTicket();
    if (!ticket) return 'Reportar un problema';
    return 'Editar ticket';
  });

  readonly modalConfirmText = computed(() => {
    const ticket = this.editingTicket();
    if (!ticket) return 'Enviar ticket';
    return 'Guardar cambios';
  });

  private readonly relativeFormatter = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });

  constructor() {
    // Move focus into the detail panel when it opens, and back to the row when it closes
    effect(() => {
      const open = !!this.selected();
      const btn = this.detailCloseBtn();
      if (open && btn) {
        btn.nativeElement.focus();
      } else if (!open && this.returnFocusTo) {
        this.returnFocusTo.focus?.();
        this.returnFocusTo = null;
      }
    });
  }

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Soporte',
      subtitle: 'Escríbenos si algo no funciona o tienes una duda',
    });
    // Links in support emails open the ticket directly: /user/support?ticket=<id>
    this.loadTickets(this.route.snapshot.queryParamMap.get('ticket') ?? undefined);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selected() && !this.modalOpen() && !this.confirmDeleteOpen() && !this.confirmCloseOpen()) {
      this.closeDetail();
    }
  }

  // ---- Labels used by the template ----

  readonly code = ticketCode;
  readonly categoryLabel = categoryLabel;
  readonly impactLabel = impactLabel;
  readonly statusLabel = statusLabel;

  isFinished(ticket: SupportTicket): boolean {
    return FINISHED.includes(ticket.status);
  }

  canEdit(ticket: SupportTicket): boolean {
    return ticket.status === 'open' || ticket.status === 'waiting_user';
  }

  /** Where each lifecycle step stands for a ticket */
  stepState(ticket: SupportTicket, index: number): StepState {
    if (ticket.status === 'closed') return 'todo';
    if (ticket.status === 'resolved') return 'done';
    const current = this.steps.findIndex((s) => s.status === ticket.status);
    if (index < current) return 'done';
    return index === current ? 'current' : 'todo';
  }

  /** Date shown next to a step in the detail timeline, when we know it */
  stepDate(ticket: SupportTicket, index: number): string | null {
    const status = this.steps[index].status;
    if (status === 'open') return ticket.createdAt;
    if (status === 'resolved') return ticket.resolvedAt;
    return null;
  }

  lastActivity(ticket: SupportTicket): string {
    const date = ticket.updatedAt ?? ticket.createdAt;
    const prefix = ticket.updatedAt ? 'Actualizado' : 'Enviado';
    return `${prefix} ${this.relative(date)}`;
  }

  rowLabel(ticket: SupportTicket): string {
    return `Ticket ${this.code(ticket.ticketNumber)}: ${ticket.subject}. ${this.statusLabel(ticket.status)}`;
  }

  // ---- Detail ----

  openDetail(ticket: SupportTicket): void {
    this.returnFocusTo = document.activeElement as HTMLElement | null;
    this.selectTicket(ticket.supportTicketId);
  }

  closeDetail(): void {
    this.selectedId.set(null);
    this.messages.set([]);
    this.replyText.set('');
    if (this.route.snapshot.queryParamMap.has('ticket')) {
      this.router.navigate([], { queryParams: { ticket: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }
  }

  focusReply(): void {
    this.replyBox()?.nativeElement.focus();
  }

  sendReply(ticket: SupportTicket): void {
    const body = this.replyText().trim();
    if (!body || this.sendingReply()) return;
    const wasWaiting = ticket.status === 'waiting_user';
    this.sendingReply.set(true);
    this.supportService
      .addMessage(ticket.supportTicketId, body)
      .pipe(finalize(() => this.sendingReply.set(false)))
      .subscribe({
        next: () => {
          this.replyText.set('');
          this.toastService.show(wasWaiting ? 'Respuesta enviada' : 'Mensaje enviado', 'success');
          this.loadMessages(ticket.supportTicketId);
          this.loadTickets(ticket.supportTicketId);
        },
        error: (err) => this.showError(err, 'No se pudo enviar el mensaje. Inténtalo de nuevo.'),
      });
  }

  private selectTicket(ticketId: string): void {
    if (this.selectedId() !== ticketId) {
      this.messages.set([]);
      this.replyText.set('');
    }
    this.selectedId.set(ticketId);
    this.loadMessages(ticketId);
  }

  private loadMessages(ticketId: string): void {
    this.messagesLoading.set(true);
    this.supportService
      .listMessages(ticketId)
      .pipe(finalize(() => this.messagesLoading.set(false)))
      .subscribe({
        next: (messages) => {
          if (this.selectedId() === ticketId) this.messages.set(messages);
        },
        error: (err) => this.showError(err, 'No se pudo cargar la conversación.'),
      });
  }

  showFirstWaiting(): void {
    const ticket = this.waitingTickets()[0];
    if (!ticket) return;
    this.filter.set('active');
    this.openDetail(ticket);
  }

  // ---- Create / edit ----

  openCreate(): void {
    this.editingTicket.set(null);
    this.modalBodyInputs = { ticket: null };
    this.modalOpen.set(true);
  }

  openEdit(ticket: SupportTicket): void {
    this.editingTicket.set(ticket);
    this.modalBodyInputs = { ticket };
    this.modalOpen.set(true);
  }

  closeModal(): void {
    this.modalOpen.set(false);
    this.editingTicket.set(null);
  }

  onModalConfirm(value: unknown): void {
    const payload = value as SupportTicketFormValue | null;
    if (!payload || this.saving()) return;
    const { supportTicketId, ...request } = payload;
    this.saving.set(true);

    if (supportTicketId) {
      this.supportService
        .updateTicket(supportTicketId, request)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: () => {
            this.toastService.show('Cambios guardados', 'success');
            this.closeModal();
            this.loadTickets(supportTicketId);
          },
          error: (err) => this.showError(err, 'No se pudieron guardar los cambios. Inténtalo de nuevo.'),
        });
    } else {
      this.supportService
        .createTicket(request)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: (res) => {
            this.toastService.show(`Ticket ${this.code(res.ticketNumber)} enviado. Te responderemos por correo.`, 'success');
            this.closeModal();
            this.filter.set('active');
            this.loadTickets(res.id);
          },
          error: (err) => this.showError(err, 'No se pudo enviar el ticket. Inténtalo de nuevo.'),
        });
    }
  }

  // ---- Status changes ----

  onConfirmClose(): void {
    const ticket = this.selected();
    if (!ticket) return;
    this.supportService.changeStatus(ticket.supportTicketId, 'closed').subscribe({
      next: () => {
        this.toastService.show('Ticket cerrado', 'success');
        this.loadTickets(ticket.supportTicketId);
      },
      error: (err) => this.showError(err, 'No se pudo cerrar el ticket. Inténtalo de nuevo.'),
    });
  }

  reopen(ticket: SupportTicket): void {
    this.supportService.changeStatus(ticket.supportTicketId, 'open').subscribe({
      next: () => {
        this.toastService.show('Ticket reabierto', 'success');
        this.filter.set('active');
        this.loadTickets(ticket.supportTicketId);
      },
      error: (err) => this.showError(err, 'No se pudo reabrir el ticket. Inténtalo de nuevo.'),
    });
  }

  onConfirmDelete(): void {
    const ticket = this.selected();
    if (!ticket) return;
    this.supportService.deleteTicket(ticket.supportTicketId).subscribe({
      next: () => {
        this.toastService.show('Ticket eliminado', 'success');
        this.closeDetail();
        this.loadTickets();
      },
      error: (err) => this.showError(err, 'No se pudo eliminar el ticket. Inténtalo de nuevo.'),
    });
  }

  // ---- Data ----

  private loadTickets(keepSelected?: string): void {
    this.loading.set(true);
    this.supportService
      .listTickets()
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.loaded.set(true);
        })
      )
      .subscribe({
        next: (tickets) => {
          this.tickets.set(tickets.map((t) => this.normalize(t)));
          const match = keepSelected ? this.tickets().find((t) => t.supportTicketId === keepSelected) : undefined;
          if (match) {
            if (FINISHED.includes(match.status)) this.filter.set('finished');
            this.selectTicket(match.supportTicketId);
          }
        },
        error: (err) => this.showError(err, 'No se pudieron cargar tus tickets. Recarga la página.'),
      });
  }

  private normalize(ticket: SupportTicket): SupportTicket {
    return {
      ...ticket,
      status: (ticket.status?.toLowerCase() ?? 'open') as SupportTicketStatus,
      messageCount: ticket.messageCount ?? 0,
    };
  }

  private relative(iso: string): string {
    const diffMs = new Date(iso).getTime() - Date.now();
    if (isNaN(diffMs)) return '';
    const minutes = Math.round(diffMs / 60000);
    if (Math.abs(minutes) < 60) return this.relativeFormatter.format(minutes, 'minute');
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return this.relativeFormatter.format(hours, 'hour');
    const days = Math.round(hours / 24);
    if (Math.abs(days) < 30) return this.relativeFormatter.format(days, 'day');
    return `el ${new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }

  private showError(err: any, fallback: string): void {
    const message = err?.error?.errors?.[0] || err?.error?.message || fallback;
    this.toastService.show(message, 'warning');
  }
}
